import _ from 'lodash';
import type { NextPage } from 'next';
import { useCallback } from 'react';
import { getPageLanguage } from '@app/utils';
import {
  Filters,
  HadronConfig,
  IParams,
  isPartnerProfileSlugError,
  Language,
  PartnerProfileEnriched,
  PROFILE_PAGE_TYPES,
  ProfilePageTypes,
  SEOProps,
  SortingMethod,
  UserDetails,
  WixNextApiRequest,
  WixNextApiResponse,
} from '@common/types';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { ProfileContextProvider } from '@components/Contexts/ProfileContext';
import { TemplatesContextProvider } from '@components/Contexts/TemplatesContext';
import NotFound from '@components/Profile/NotFound';
import NotValidEuEmptyState from '@components/Profile/NotValidEuEmptyState';
import Profile from '@components/Profile';
import ProfileHeader from '@components/ProfileHeader';
import createCurrencyAPI from '@services/CurrencyAPI';
import createLanguagesAPI from '@services/LanguagesAPI';
import createPartnerTraceableTraderAPI from '@services/PartnerTraceableTraderAPI';
import createPetriServiceAPI from '@services/PetriServiceAPI';
import createSeoAPI from '@services/ProfileSeoAPI';
import createProvidersAPI from '@services/ProvidersAPI';
import createServicesAPI from '@services/ServicesAPI';
import createTemplateListingAPI from '@services/TemplateListingAPI';
import createUserAPI from '@services/UserAPI';
import {
  PROFILE_NOT_EU_VALID,
  PROFILE_NOT_FOUND,
  IS_MY_PROFILE_BY_ACCOUNT_ID_INSTEAD_OF_SLUG,
} from '@utils/constants';
import { Service } from '@wix/ambassador-marketplace-categories-v1-category/types';
import { WixDesignSystemProvider } from '@wix/design-system';
import { GeoData, web } from '@wix/wix-common-aspects';
import log from '@wix/wix-log';
import he from 'he';
import { getCurrencyPreferences } from '../hire/[[...all]]';
import { Type } from '@wix/ambassador-marketplace-provider-state/types';

const WixPartner: NextPage<{
  partnerProfile: PartnerProfileEnriched;
  services: Array<Service>;
  categoriesForHeader: Array<Service> | undefined;
  staticLocaleUrl: string;
  geo: GeoData;
  language: string;
  languages: Array<Language>;
  origin: string;
  entry: any;
  isMyProfile: boolean;
  enabled: boolean;
  isRenderedInModal: boolean;
  isMobileUserAgent: boolean;
  error: string;
  host: string;
  seo: SEOProps;
  userCurrency: string;
  currencyConversionRate: number;
  currencyPreferences: string;
  shouldDisplayTraceabilityData: boolean;
  taxRate: number;
  numberOfTemplates: number;
  templatesNumberOfPages: number;
  filters: Filters;
  currentPage: number;
  sort: SortingMethod;
  templatesAverageScore: number;
  profilePageType: ProfilePageTypes;
  userDetails: UserDetails;
}> = ({
  partnerProfile,
  services,
  categoriesForHeader,
  staticLocaleUrl,
  geo,
  language,
  languages,
  origin,
  entry,
  isMyProfile,
  enabled,
  isRenderedInModal,
  isMobileUserAgent,
  host,
  error,
  seo: { title, metaTags, links, structureData, gtm },
  userCurrency,
  currencyConversionRate,
  currencyPreferences,
  shouldDisplayTraceabilityData,
  taxRate,
  numberOfTemplates,
  templatesNumberOfPages,
  filters,
  currentPage,
  sort,
  templatesAverageScore,
  profilePageType,
  userDetails,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const renderProfile = useCallback(() => {
    if (error === PROFILE_NOT_FOUND) {
      return <NotFound />;
    } else if (error === PROFILE_NOT_EU_VALID) {
      return <NotValidEuEmptyState />;
    } else {
      return <Profile />;
    }
  }, [error]);

  const ProfileWithContexts = () => {
    const profile = (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        {renderProfile()}
      </WixDesignSystemProvider>
    );

    return (
      <CurrencyContextProvider
        currencyData={{
          currencyCode: userCurrency,
          conversionRate: currencyConversionRate,
        }}
        taxRate={taxRate}
      >
        {profile}
      </CurrencyContextProvider>
    );
  };

  return (
    <>
      <ProfileHeader
        title={title}
        links={links}
        metaTags={metaTags}
        structureData={structureData}
        gtm={gtm}
        isProd={isProd}
      />
      <BiSessionContextProvider>
        <HeaderContextProvider
          services={categoriesForHeader || services}
          host={host}
          userCurrency={userCurrency}
        >
          <ProfileContextProvider
            partnerProfile={partnerProfile}
            services={services}
            staticLocaleUrl={staticLocaleUrl}
            geo={geo}
            language={language}
            languages={languages}
            origin={origin}
            host={host}
            entry={entry}
            isMyProfile={isMyProfile}
            enabled={enabled}
            isRenderedInModal={isRenderedInModal}
            isMobileUserAgent={isMobileUserAgent}
            userCurrency={userCurrency}
            currencyConversionRate={currencyConversionRate}
            preferredCurrency={currencyPreferences}
            shouldDisplayTraceabilityData={shouldDisplayTraceabilityData}
            profilePageType={profilePageType}
            userDetails={userDetails}
          >
            <TemplatesContextProvider
              numberOfResults={numberOfTemplates}
              numberOfPages={templatesNumberOfPages}
              isMobileUserAgent={isMobileUserAgent}
              origin={origin}
              entry={entry}
              host={host}
              filters={filters}
              currentPage={currentPage}
              sort={sort}
              templatesAverageScore={templatesAverageScore ?? 0}
            >
              <ProfileWithContexts />
            </TemplatesContextProvider>
          </ProfileContextProvider>
        </HeaderContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  query,
  params,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
  params: any;
}) {
  const isProd = process.env.NODE_ENV === 'production';
  const isRenderedInModal = query?.inModal === 'true';
  const host = isProd ? `https://${req.get('host')}` : `http://localhost:3000`;

  const servicesAPI = createServicesAPI(res.locals.appContext, req.aspects);
  const partnerTraceableTraderAPI = createPartnerTraceableTraderAPI(
    res.locals.appContext,
    req.aspects,
  );
  const petriServiceAPI = createPetriServiceAPI(
    res.locals.appContext,
    req.aspects,
  );
  const templateListingAPI = createTemplateListingAPI(
    res.locals.appContext,
    req.aspects,
  );
  const userAPI = createUserAPI(res.locals.appContext, req.aspects);

  const language = getPageLanguage({ aspects: req.aspects });
  const geo = req.aspects?.get(web.Geo);
  const url = req.aspects?.get(web.Url);
  const alpha2CountryCode = geo?.['2lettersCountryCode'];
  let shouldDisplayTraceabilityData = false;

  const currencyAPI = createCurrencyAPI(res.locals.appContext, req.aspects);
  const currencyPreferences = await getCurrencyPreferences({
    alpha2CountryCode,
    currencyAPI,
    req,
    res,
  });

  const providersAPI = createProvidersAPI(res.locals.appContext, req.aspects);
  const languagesAPI = createLanguagesAPI(res.locals.appContext, req.aspects);
  const loggedInAccountId = await userAPI.getLoggedInAccountId();

  try {
    const { slug } = params;

    const [
      services,
      categoriesForHeader,
      traceabilityCountries,
      languages,
      userDetails,
    ] = await Promise.all([
      servicesAPI.getAllServicesWithHierarchy({
        withPathName: true,
        withTranslatedKey: true,
        language,
      }),
      servicesAPI.getCategoriesForHeader({
        withPathName: true,
        withTranslatedKey: true,
        language,
      }),
      partnerTraceableTraderAPI.getTraceabilityCountries(),
      languagesAPI.getLanguages(),
      userAPI.getUserDetails(),
    ]);

    if (he.decode(slug as string) === 'wix-demo-agency') {
      const partnerProfile = require('./demo-agency.json');
      const seoAPI = await createSeoAPI({
        partnerProfile,
        language,
        url,
      });
      return {
        props: {
          host,
          seo: {
            title: seoAPI.getPageTitle(),
            metaTags: seoAPI.getPageMetaTags(),
            links: seoAPI.getPageLinkTags(),
            structureData: JSON.parse(
              JSON.stringify(seoAPI.getProfileStructureData(partnerProfile)),
            ),
            gtm: seoAPI.getProfileGTM(),
          },
          slug,
          partnerProfile,
          services,
          language,
          languages,
          numberOfTemplates: 6,
          templatesNumberOfPages: 1,
        },
      };
    }

    let partnerProfile = await providersAPI.findPartnerProfileBySlug(
      he.decode(slug as string),
    );

    let haveTraceabilityViolation = false;

    if (!isPartnerProfileSlugError(partnerProfile)) {
      const isUserInTraceabilityRequiredLocale = _.includes(
        traceabilityCountries,
        alpha2CountryCode,
      );

      const isPartnerEuValid = Boolean(partnerProfile?.euValid);

      shouldDisplayTraceabilityData =
        isUserInTraceabilityRequiredLocale && isPartnerEuValid;

      haveTraceabilityViolation =
        isUserInTraceabilityRequiredLocale && !isPartnerEuValid;
    }

    if (isPartnerProfileSlugError(partnerProfile)) {
      return {
        props: {
          partnerProfile: {},
          services,
          categoriesForHeader,
          error: partnerProfile?.error,
          seo: {
            title: 'Profile Not Found - Wix Marketplace',
          },
          host,
        },
      };
    }

    const hasProviderState = !_.isEmpty(partnerProfile?.state);
    const isAvailable = partnerProfile?.state?.enabled;
    const isPublished = partnerProfile?.state?.statusType === Type.PUBLISHED;
    const isMPPageQualified =
      hasProviderState &&
      isAvailable &&
      isPublished &&
      !haveTraceabilityViolation;

    const profilePageType = isMPPageQualified
      ? PROFILE_PAGE_TYPES.marketplace
      : PROFILE_PAGE_TYPES.agency;

    const { entry, origin } = query;
    const currentPartnerProfile = await providersAPI.getCurrentPartnerProfile();
    const isEnabledIsMyProfileByAccountIdInsteadOfSlug =
      await petriServiceAPI.isExperimentEnabled(
        IS_MY_PROFILE_BY_ACCOUNT_ID_INSTEAD_OF_SLUG,
      );
    const isMyProfile = isEnabledIsMyProfileByAccountIdInsteadOfSlug
      ? loggedInAccountId === partnerProfile.id
      : currentPartnerProfile?.slug === slug;

    const seoAPI = await createSeoAPI({
      partnerProfile,
      language,
      url,
    });

    const config = res.locals.appContext.config.load<HadronConfig>(
      'experts-marketplace-nextjs-template.json',
    );
    const staticLocaleUrl = await res.locals.hadron.staticUrl(
      'com.wixpress.locale-dataset-data',
      config.clientTopology.locale_dataset_data_static_url,
    );

    const { userCurrency, currencyConversionRate, countryTaxRate } =
      await currencyAPI.getGeoBasedTaxAndCurrencyRate({
        aspects: req.aspects,
        loggerName: 'wix-partner',
      });

    const filters = {
      languages: [],
      language: 'ALL',
      features: [],
      industries: [],
      siteType: 'ALL',
      styles: [],
      price: 'ALL',
      search: '',
    } as Filters;

    const { templateListingCount, templatesAverageScore } =
      await templateListingAPI.getPartnerTemplateAggregate({
        partnerId: partnerProfile.id!,
        page: 0,
        ...filters,
      });
    const templatesNumberOfPages = templateListingCount
      ? _.ceil(templateListingCount / 6)
      : 0;
    const props = {
      slug,
      ...(partnerProfile && {
        partnerProfile: JSON.parse(JSON.stringify(partnerProfile)),
      }),
      enabled: partnerProfile?.state?.enabled || false,
      language,
      languages,
      ...(geo && { geo }),
      services,
      categoriesForHeader,
      staticLocaleUrl,
      ...(origin && { origin }),
      ...(entry && { entry }),
      isMyProfile,
      isRenderedInModal,
      host,
      seo: {
        title: seoAPI.getPageTitle(),
        metaTags: seoAPI.getPageMetaTags(),
        links: seoAPI.getPageLinkTags(),
        structureData: JSON.parse(
          JSON.stringify(seoAPI.getProfileStructureData(partnerProfile)),
        ),
        gtm: seoAPI.getProfileGTM(),
      },
      userCurrency,
      currencyConversionRate,
      ...(currencyPreferences && { currencyPreferences }),
      shouldDisplayTraceabilityData,
      taxRate: countryTaxRate,
      numberOfTemplates: templateListingCount,
      templatesNumberOfPages,
      templatesAverageScore: templatesAverageScore ?? 0,
      filters,
      currentPage: 1,
      sort: 'relevance' as SortingMethod,
      profilePageType,
      ...(userDetails && { userDetails }),
    };

    return {
      props,
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('wix-partner')
      .withAspects(req.aspects)
      .error(`error w/ wix-partner`, { message, stack });
    return {
      props: { message, stack, partnerProfile: {}, seo: {} },
    };
  }
}

export default WixPartner;
